import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  toggleSelectedCrewMemberAction,
  setSelectedCrewDays,
  setSelectedCrewEvents,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { openContextMenu } from "src/redux/actions/ContextMenu.Actions";
import classNames from "classnames";
import { baseRowHeight, expandedRowHeight } from "src/pages/CrewSchedule/gl/CrewScheduleGL";
import { PositionBadge } from "./PositionBadge";
import CrewMemberName from "./PairingEditorWindow/CrewMemberName";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import Tooltip from "@mui/material/Tooltip";
import { IModalInfo } from "src/pages/CrewSchedule/CrewScheduleWindow";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { Grid, useTheme, Theme, Typography, Popover, Button } from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  returnAllStageCredit,
  returnAssignedCredit,
  returnAssignedNFATypes,
  returnAllStagedNFATypes,
  returnAllStagePlannedCreditMap,
} from "src/redux/reducers/BiddingReducer";
import { IconButton } from "src/components/IconButton/IconButton";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrow";
import DownloadIcon from "@mui/icons-material/GetApp";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import {
  updateCrewBids,
  getCrewBids,
  gotCrewMemberBids,
  toggleAssignmentCrewBidRank,
} from "src/redux/actions/Bidding.Actions";
import { debounce } from "lodash";
import {
  ICrewBids,
  IDisplayBidPairing,
  IDisplayBidNFA,
  IAwardBiddingPayload,
  IBidResponse,
  IPosition,
  ViolationCheckState,
  CreditSource,
  CreditSurface,
  getCreditClientSettingsByPosition,
  resolveCreditSource,
} from "@vmosolutions/oc-interfaces";
import { returnAllAssignedBids, resetAllAssignedBids } from "src/redux/reducers/BiddingReducer";
import CircularProgress from "@mui/material/CircularProgress";
import EditRounded from "@mui/icons-material/EditRounded";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { formatDateString } from "../DateDisplay";
import { ShortCodeChip } from "../ShortCodes/ShortCodeChip";
import { openSnackbar } from "src/redux/actions/App.Actions";

interface ICrewMemberBiddingProps {
  id?: string;
  crewMember: ICrewMember;
  bidRankSettings?: [{ prefRank: number; label: string; color: string }]; // based off of IBidSettingsValue.ranks
  bidAssignmentMode?: boolean;
  setActiveEventModal?: (modal: IModalInfo | null) => void;
  displayInHeader?: boolean; // set true if being used in the header component of the bidding assignment tool (default context is crew list);
  displayPlannedCredit?: boolean;
  displayAssignedNFATypes?: string[];
}

/**
 * Both crewBidRowHeight and getSelectedCrewBidRowHeight are exported for use in the Crew ScheduleGL.
 * @param rankCount - Function used to determine height of selected crew row (in both this component and CrewScheduleGL)
 * @returns - return rank count (each rank 16px height) plus margins. (120px for three ranks)
 */
export const crewBidRowHeight = 14;
export const getSelectedCrewBidRowHeight = (rankCount: number): number => {
  // return crew name height + staging row height + total rank row height
  return 72 + 16 + (crewBidRowHeight + 2) * rankCount;
};

const changeDetailsPopupStyles = (theme: Theme): object => ({
  "& .MuiPopover-paper": {
    width: "350px",
    marginLeft: "5px",
    backgroundColor: "rgba(0,0,0,0.75)",
    "& .crew_bid_change_status": {
      margin: "10px",
      "& .bid_status": {
        "&.award svg": { transform: "rotate(-90deg)" },
        "& svg": { width: "16px", height: "16px", transform: "rotate(90deg)" },
      },
    },
    "& .bid_pairing": {
      "& .position_badge_wrapper": {
        height: "auto",
      },
    },

    // Coloring for assigned bid pairings
    "& .bid_pairing.assigned": {
      "& .badge": { color: "black", backgroundColor: theme.custom.forms.dirtyColor },
      "& .pairing": {
        backgroundColor: theme.custom.forms.dirtyColor,
        "& svg path": { fill: theme.custom.forms.dirtyColor },
        "& .shortcode": { color: "black" },
      },
    },

    // Coloring for removed (unrewarded bid pairings)
    "& .bid_pairing.removed": {
      "& .badge": { backgroundColor: "orangered" },
      "& .pairing": {
        backgroundColor: "orangered",
        "& svg path": { fill: "orangered" },
        "& .shortcode": { color: "white" },
      },
    },

    "& .bidpackage_nfa_assignments": {
      width: "auto",
      margin: "5px",
      "& .bid_nfa.assigned": {
        "& .nfa_badge": { backgroundColor: `${theme.custom.forms.dirtyColor} !important` },
        "& span": { color: theme.custom.forms.dirtyColor },
      },
      "& .bid_nfa.removed": {
        "& .nfa_badge": {
          backgroundColor: "orangered !important",
          "& div": { color: "white" },
        },
        "& span": { color: "orangered" },
      },
      "& .nfa_badge": {
        height: "16 px",
        borderRadius: "3px",
        padding: "0px 5px",
        marginRight: "10px",
        "& div": { color: "black", fontSize: 10, marginTop: "2px" },
      },
    },
  },
});

const crewBiddingInfoStyles = (theme: Theme): object => ({
  "&.bidding_done": {
    backgroundColor: `${theme.custom.bidding.activeBackgroundColor} !important`,
    backgroundImage: "none !important",
  },
  "& .reset_all_changes_btn": {
    height: "19px",
    margin: "0px 5px !important",
    fontSize: 10,
  },
  "& .show_change_details_btn": {
    height: "20px",
    width: "100px",
    margin: "0px 5px !important",
    fontSize: 10,
  },
  "& .toggle_bidding_done_checkbox": { margin: "0px 2px", width: "140px" },
  "& .crew_member": {
    width: "100%",
    height: "100%",
    "& .edit_text": {
      "&.active": {
        color: theme.custom.forms.dirtyColor,
      },
    },
    "& .MuiSvgIcon-root.edit_icon": {
      width: "14px",
      height: "14px",
      border: `1px solid ${theme.custom.forms.dirtyColor}`,
      borderRadius: "3px",
      margin: "0px 5px",
      color: theme.custom.forms.dirtyColor,
    },
    "& .pref_rank_bar": {
      height: crewBidRowHeight,
      width: "115px",
      margin: "2px",
      position: "relative",
      "& .label": {
        color: "black",
        marginLeft: "5px",
        fontWeight: 900,
        lineHeight: "normal",
      },
      "& .toggle_stage_assignment_btn": {
        padding: 0,
        margin: 0,
        color: "black",
        position: "absolute",
        right: "20px",
        "&.assign": {
          right: 0,
          "& svg": {
            transform: "rotate(-90deg)",
          },
        },
        "&.Mui-disabled": { opacity: 0.5 },
        "& svg": {
          transform: "rotate(90deg)",
          marginRight: "5px",
          width: "14px",
          height: "14px",
        },
      },
    },
    "& .save_awarded_bids_btn, & .load_crew_bids_btn": {
      height: "20px",
      width: "30px",
      marginTop: "10px",
      borderRadius: "3px",
      color: "black",
      backgroundColor: "white",
      "& .MuiCircularProgress-root svg circle": { color: "black" },
      "& svg": { width: "14px", height: "14px", fill: "black" },
      "&.dirty": { backgroundColor: theme.custom.forms.dirtyColor },
      "&.Mui-disabled": { opacity: 0.5 },
    },
    "& .save_awarded_bids_btn": {
      backgroundColor: theme.custom.forms.dirtyColor,
    },
    "& .awarded_unsaved_count": { color: theme.custom.forms.dirtyColor },
  },
});

// const crewNameSpace = 80; // This accounts for the space required to display crew badges+padding

/**
 * Function that returns a count of a selected crew members total bids, bids of a specific rank, and/or assigned bids.
 * @param crewMemberBids - crew members bids object
 * @param rank - optional rank (filters count by rank if passed)
 * @param onlyAssigned - optional assigned only count
 * @returns
 */
const bidEventCounts = (crewMemberBids: ICrewBids, rank?: number, onlyAssigned?: boolean): number => {
  if (!crewMemberBids?.bids) {
    return 0;
  }
  let crewBidPairings: IDisplayBidPairing[] = [];
  let crewBidNFAs: IDisplayBidNFA[] = [];
  for (const [prefRank, bids] of Object.entries(crewMemberBids.bids)) {
    if ((!!rank && rank.toString() === prefRank.toString()) || !rank) {
      const filteredBidPairings = onlyAssigned
        ? bids?.displayBidPairings?.filter((bp) => bp.assigned)
        : bids?.displayBidPairings;
      crewBidPairings = [...crewBidPairings, ...filteredBidPairings];

      const filteredBidNFAs = onlyAssigned ? bids?.displayBidNFAs?.filter((bp) => bp.assigned) : bids?.displayBidNFAs;
      crewBidNFAs = [...crewBidNFAs, ...filteredBidNFAs];
    }
  }
  return crewBidPairings?.length + crewBidNFAs?.length;
};

/**
 * Function that returns a count of a selected crew members total bids, bids of a specific rank, and/or assigned bids.
 * @param crewMemberBids - crew members bids object
 * @param rank - optional rank (filters count by rank if passed)
 * @param onlyAssigned - optional assigned only count
 * @returns
 */
const crewBidDetails = (crewMemberBids: ICrewBids, hideNfaPriority?: boolean): JSX.Element => {
  if (!crewMemberBids?.bids) {
    return <Typography>No Crew Bids Available</Typography>;
  }

  let crewBidPairings: IDisplayBidPairing[] = [];
  let crewBidNFAs: IDisplayBidNFA[] = [];

  for (const [prefRank, bids] of Object.entries(crewMemberBids.bids)) {
    crewBidPairings = [...crewBidPairings, ...bids.displayBidPairings];
    crewBidNFAs = [...crewBidNFAs, ...bids.displayBidNFAs];
  }

  const totalBidPairings = crewBidPairings?.length;
  const awardeddBPs = crewBidPairings?.filter((bp) => bp.awarded);
  const assignedBPs = crewBidPairings?.filter((bp) => bp.assigned && !bp.awarded);
  const removedPBs = crewBidPairings?.filter((bp) => bp.assigned && bp.awarded);

  const totalBidNFAs = crewBidNFAs?.length;
  const awardedBNFAs = crewBidNFAs?.filter((bnfa) => bnfa.awarded);
  const assignedBNFAs = crewBidNFAs?.filter((bnfa) => bnfa.assigned && !bnfa.awarded);
  const removedBNFAs = crewBidNFAs?.filter((bnfa) => bnfa.assigned && bnfa.awarded);

  return (
    <Grid container direction="column">
      {totalBidPairings === 0 ? (
        <Typography variant="caption" style={{ margin: 5 }}>
          No Bid Pairings Available
        </Typography>
      ) : (
        <>
          <Grid container className="bid_pairing_details awarded" wrap="nowrap">
            <Typography variant="caption" style={{ marginLeft: 16 }}>
              {awardeddBPs?.length + assignedBPs?.length} pairing bids staged or awarded of {totalBidPairings} total.
            </Typography>
          </Grid>

          {assignedBPs?.length > 0 && (
            <Grid
              container
              className="bid_status award"
              style={{ color: "lightgreen" }}
              alignItems="center"
              wrap="nowrap"
            >
              <DoubleArrowIcon color="inherit" />
              <Typography variant="caption" style={{ color: "inherit" }}>
                {assignedBPs?.length ?? 0} pairing bids staged for awarding.
              </Typography>
            </Grid>
          )}

          {removedPBs?.length > 0 && (
            <Grid container className="bid_status" style={{ color: "orangered" }} alignItems="center" wrap="nowrap">
              <DoubleArrowIcon color="inherit" />
              <Typography variant="caption" style={{ color: "inherit" }}>
                {removedPBs?.length ?? 0} pairing bids staged for unawarding.
              </Typography>
            </Grid>
          )}

          <Grid container style={{ margin: "10px 0px" }}>
            {crewBidPairings
              ?.sort((a, b) => a.start?.valueOf() - b.start?.valueOf())
              ?.filter((bp) => bp.assigned || bp.awarded)
              ?.map((bp) => {
                const awardeddBP = !bp.assigned && bp.awarded;
                const assignedBP = bp.assigned && !bp.awarded;
                const status = awardeddBP ? "awarded" : assignedBP ? "assigned" : "removed";

                return (
                  <Grid
                    container
                    className={`bid_pairing ${status}`}
                    key={`bid_pairing_${bp._id}_${bp.prefRank}_${bp.positionBidOn}_${status}`}
                    style={{ width: "auto", margin: 2 }}
                    alignItems="center"
                  >
                    <PositionBadge positionTitle={bp.positionBidOn} filled={assignedBP ? false : true} />
                    <ShortCodeChip code={bp.shortId} itemId={bp._id} type="pairing" />
                  </Grid>
                );
              })}
          </Grid>
        </>
      )}

      {totalBidNFAs === 0 ? (
        <Typography variant="caption" style={{ margin: 5 }}>
          No Bid NFAs Available
        </Typography>
      ) : (
        <>
          <Grid container className="bid_nfa_details awarded" wrap="nowrap">
            <Typography variant="caption" style={{ marginLeft: 16 }}>
              {awardedBNFAs?.length + assignedBNFAs?.length} NFA bids staged or awarded of {totalBidNFAs} total.
            </Typography>
          </Grid>

          {assignedBNFAs?.length > 0 && (
            <Grid
              container
              className="bid_status award"
              style={{ color: "lightgreen" }}
              alignItems="center"
              wrap="nowrap"
            >
              <DoubleArrowIcon color="inherit" />
              <Typography variant="caption" style={{ color: "inherit" }}>
                {assignedBNFAs?.length ?? 0} NFA bids staged for awarding.
              </Typography>
            </Grid>
          )}

          {removedBNFAs?.length > 0 && (
            <Grid container className="bid_status" style={{ color: "orangered" }} alignItems="center" wrap="nowrap">
              <DoubleArrowIcon color="inherit" />
              <Typography variant="caption" style={{ color: "inherit" }}>
                {removedBNFAs?.length ?? 0} NFA bids staged for unawarding.
              </Typography>
            </Grid>
          )}

          <Grid container className="bidpackage_nfa_assignments" style={{ margin: "10px 0px" }}>
            {crewBidNFAs
              ?.sort((a, b) => a.start?.valueOf() - b.start?.valueOf())
              ?.filter((bnfa) => bnfa.assigned || bnfa.awarded)
              ?.map((bnfa) => {
                const awardeddBP = !bnfa.assigned && bnfa.awarded;
                const assignedBP = bnfa.assigned && !bnfa.awarded;
                const status = awardeddBP ? "awarded" : assignedBP ? "assigned" : "removed";

                return (
                  <Grid
                    container
                    className={`bid_nfa ${status}`}
                    key={`${bnfa._id}_${bnfa.prefRank}_${status}`}
                    style={{ width: "auto", margin: 2 }}
                    alignItems="center"
                  >
                    <Tooltip key={`nfa_${bnfa?.assignmentType?._id}`} title={`${bnfa?.assignmentType?.description}`}>
                      <Grid
                        item
                        className="nfa_badge"
                        style={{ backgroundColor: `#${bnfa?.assignmentType?.defaultColor}` }}
                      >
                        <div>{`${bnfa?.assignmentType?.title}${!hideNfaPriority && typeof bnfa?.tier === "number" ? ` ${bnfa.tier}` : ""}`}</div>
                      </Grid>
                    </Tooltip>
                    <Typography variant="caption">
                      {bnfa.start ? formatDateString(new Date(bnfa.start), "Etc/UTC", false) : ""}
                    </Typography>
                  </Grid>
                );
              })}
          </Grid>
        </>
      )}
    </Grid>
  );
};

/**
 * A function that takes in two postions and returns the one with the highest display order
 * @param currentRecent-
 * @param incoming-
 */
const highestDisplayOrderPostion = (current: IPosition, incoming: IPosition): IPosition => {
  return current?.displayOrder > incoming?.displayOrder ? incoming : current;
};

export const CrewMemberBidding = (props: ICrewMemberBiddingProps): JSX.Element => {
  const {
    crewMember,
    id,
    bidRankSettings,
    bidAssignmentMode,
    setActiveEventModal,
    displayInHeader,
    displayPlannedCredit,
    displayAssignedNFATypes,
  } = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState<Element>();

  // Bidding related data.
  const crewBids = useSelector((state: IRootState) => state.bidding.crewBids);
  const bidPackagePosition = useSelector(
    (state: IRootState) => state.bidding.selectedBidPackage.bidPackage?.positions[0]
  );
  const hideNfaPriority = useSelector(
    (state: IRootState) => state.crewSchedule.biddingSettings?.hideNfaPriority ?? false
  );
  const crewMemberBidPackage = useMemo(() => crewBids?.[crewMember?._id], [crewBids, crewMember?._id]);

  const disableMouseEvents = false;
  const { isSelected, isExpanded } = crewMember ?? { isSelected: false, isExpanded: false };
  const crewNameSpace = 80; // This accounts for the space required to display crew badges+padding

  // used only in bidding mode.
  const selectedBidPackage = useSelector((state: IRootState) => state.bidding.selectedBidPackage);
  //const assignmentIsDirty = useSelector((state: IRootState) => state.bidding.assignmentIsDirty);

  // 🤑🤑 For Credit 🤑🤑
  const nonFlyingTypes = useSelector((state: IRootState) => state.structures.customerStructure.nonFlyingTypes);
  const crewNFAs = useSelector(
    (state: IRootState) => state.crewSchedule.crewNonFlyingAssignments[crewMember?._id]?.nonFlyingAssignments
  );
  const crewPairings = useSelector((state: IRootState) => state.crewSchedule.crewPairings[crewMember?._id]?.pairings);
  const creditSettingValue = useSelector((state: IRootState) => state.credit);
  const creditSetting = useMemo(() => {
    return creditSettingValue.settings ?? [];
  }, [creditSettingValue]);

  const selectedCrewGroups = useMemo(() => {
    return crewMember.crewInfo.groups?.map((cg) => cg) ?? [];
  }, [crewMember.crewInfo.groups]);

  /**
   * Does this bid package's grouping read calculated credit for assignment?
   */
  const creditAllocatedByDay: boolean = useMemo(() => {
    const setting = getCreditClientSettingsByPosition(bidPackagePosition, creditSetting, selectedCrewGroups);
    if (!setting) {
      return false;
    }
    return (
      resolveCreditSource(CreditSurface.bidAssignment, setting, creditSettingValue.calculatedCredit).source !==
      CreditSource.operatorEntered
    );
  }, [bidPackagePosition, creditSetting, creditSettingValue.calculatedCredit, selectedCrewGroups]);

  const stagedCredit: number = useMemo(() => {
    if (!crewMemberBidPackage) {
      return 0;
    }
    const start = new Date(selectedBidPackage.bidPackage?.bidBlockStart)?.valueOf();
    const end = new Date(selectedBidPackage.bidPackage?.bidBlockEnd)?.valueOf();
    // The same source rule as assignedCredit below: the credit map path whenever calculated credit is on
    // for the bidAssignment surface OR the additional planned credit flag is set. Otherwise the two halves
    // of totalPlannedCredits could read different sources for the same crew member.
    //we can adjust here if we want the logic to be that if no credit setting exists for this crew pos & group, default back to plannedCredit
    return creditAllocatedByDay || creditSettingValue.useAdditionalPlannedCreditInBidAssignment
      ? returnAllStagePlannedCreditMap(
          crewMemberBidPackage,
          start,
          end,
          creditSetting,
          bidPackagePosition,
          selectedCrewGroups,
          creditSettingValue.calculatedCredit
        )
      : returnAllStageCredit(crewMemberBidPackage, start, end);
  }, [
    bidPackagePosition,
    creditAllocatedByDay,
    creditSetting,
    creditSettingValue.useAdditionalPlannedCreditInBidAssignment,
    creditSettingValue.calculatedCredit,
    crewMemberBidPackage,
    selectedBidPackage.bidPackage?.bidBlockEnd,
    selectedBidPackage.bidPackage?.bidBlockStart,
    selectedCrewGroups,
  ]);

  //this is credit that is ACTUALLY on the schedule - it isn't caring if the items are on their schedule because of bid assignment or not
  const assignedCredit: number = useMemo(() => {
    //get the subset of pairings and NFAs in the bid period
    if (
      !selectedBidPackage ||
      !selectedBidPackage.bidPackage?.bidBlockStart ||
      !selectedBidPackage.bidPackage?.bidBlockEnd
    ) {
      return 0;
    }
    const start = new Date(selectedBidPackage.bidPackage.bidBlockStart).valueOf();
    const end = new Date(selectedBidPackage.bidPackage.bidBlockEnd).valueOf();
    return returnAssignedCredit(
      crewPairings ?? [],
      crewNFAs ?? [],
      start,
      end,
      creditSetting,
      bidPackagePosition,
      selectedCrewGroups,
      creditSettingValue.calculatedCredit,
      creditSettingValue.useAdditionalPlannedCreditInBidAssignment
    );
  }, [
    selectedBidPackage,
    creditSettingValue.calculatedCredit,
    creditSettingValue.useAdditionalPlannedCreditInBidAssignment,
    creditSetting,
    crewPairings,
    crewNFAs,
    bidPackagePosition,
    selectedCrewGroups,
  ]);

  const totalPlannedCredits = useMemo(() => {
    //round to the 2 decimal point
    return Math.round((stagedCredit + assignedCredit) * 100) / 100;
  }, [stagedCredit, assignedCredit]);

  // Check bidding client settings for any nfa types to show and verify they exist in current structures.
  const displayNFATypes: string[] = useMemo(() => {
    return !!displayAssignedNFATypes?.length && !!nonFlyingTypes?.length
      ? displayAssignedNFATypes?.filter((title) => nonFlyingTypes?.map((type) => type.title)?.includes(title))
      : [];
  }, [displayAssignedNFATypes, nonFlyingTypes]);

  // Return Count of a specified type of NFA.
  const assignedNFATypes: number[] = useMemo(() => {
    //get the subset of pairings and NFAs in the bid period
    if (
      !selectedBidPackage ||
      !selectedBidPackage.bidPackage?.bidBlockStart ||
      !selectedBidPackage.bidPackage?.bidBlockEnd ||
      !displayNFATypes?.length
    ) {
      return [];
    }
    const start = new Date(selectedBidPackage.bidPackage.bidBlockStart).valueOf();
    const end = new Date(selectedBidPackage.bidPackage.bidBlockEnd).valueOf();
    return returnAssignedNFATypes(crewNFAs ?? [], start, end, displayNFATypes);
  }, [crewNFAs, selectedBidPackage, displayNFATypes]);

  const stagedNFATypeCounts: number[] = useMemo(() => {
    if (crewMemberBidPackage && !!displayNFATypes?.length) {
      return returnAllStagedNFATypes(
        crewMemberBidPackage,
        new Date(selectedBidPackage.bidPackage?.bidBlockStart)?.valueOf(),
        new Date(selectedBidPackage.bidPackage?.bidBlockEnd)?.valueOf(),
        displayNFATypes
      );
    } else {
      const defaultCount: number[] = [];
      displayNFATypes?.forEach(() => defaultCount.push(0));
      return defaultCount;
    }
  }, [crewMemberBidPackage, selectedBidPackage, displayNFATypes]);

  const totalAssignedNFATypes: number[] = useMemo(
    () => assignedNFATypes.map((num, index) => num + stagedNFATypeCounts[index]),
    [stagedNFATypeCounts, assignedNFATypes]
  );

  const bidAssignmentHeight = useMemo(() => {
    return !displayInHeader && bidRankSettings?.length
      ? getSelectedCrewBidRowHeight(bidRankSettings?.length)
      : baseRowHeight;
  }, [bidRankSettings, displayInHeader]);

  const handleContext = useCallback(
    (crewInfoId: string) =>
      (event: React.MouseEvent): void => {
        event.preventDefault();
        //clear any selected crew sched events because I want to show the crew block hours
        dispatch(setSelectedCrewEvents([], false, false, false));
        dispatch(setSelectedCrewDays(null));
        dispatch(toggleSelectedCrewMemberAction(crewInfoId, true));
        dispatch(openContextMenu(`${id}`));
      },
    [dispatch, id]
  );

  // In bid assignment mode, selecting the crew will also display the bid assignment rows (expand).
  const toggleDisplayBidRows = useCallback(
    (crewInfoId: string, openContext?: boolean) => (event: React.MouseEvent) => {
      event.stopPropagation(); // Stop propagation of the select crew member action.
      if (disableMouseEvents) {
        return;
      }
      dispatch(setSelectedCrewEvents([], false, false, false));
      dispatch(setSelectedCrewDays(null));
      dispatch(toggleSelectedCrewMemberAction(crewInfoId, openContext, true));
      if (openContext) handleContext(crewInfoId)(event);
    },
    [disableMouseEvents, dispatch, handleContext] //assignmentIsDirty
  );

  const crewMemberStyle = useMemo(
    () =>
      !disableMouseEvents && isExpanded && !bidAssignmentMode
        ? { display: "flex", height: expandedRowHeight }
        : !disableMouseEvents && isExpanded && bidAssignmentMode
          ? { display: "flex", height: bidAssignmentHeight }
          : { display: "flex", height: baseRowHeight },
    [bidAssignmentMode, disableMouseEvents, isExpanded, bidAssignmentHeight]
  );

  /*
  const bidPackagePositions = useMemo(() => {
    return selectedBidPackage?.bidPackage?.positions?.length > 0
      ? positions?.filter((pos) => selectedBidPackage?.bidPackage?.positions.includes(pos.title))
      : [];
  }, [positions, selectedBidPackage?.bidPackage?.positions]);

  const availableCrewPositionTitles = useMemo(
    () => crewMember?.crewInfo?.positions?.map((pos) => pos.title),
    [crewMember?.crewInfo?.positions]
  );
  */

  // Retrieve available assignment positions. (intersections of package positions and crew qualifications);
  const availablePositions: IPosition[] = useMemo(() => {
    const crewPositions = crewMember?.crewInfo?.positions ?? [];
    return crewPositions.filter((pos) => selectedBidPackage?.bidPackage?.positions.includes(pos.title));
  }, [selectedBidPackage?.bidPackage?.positions, crewMember?.crewInfo?.positions]);

  // Return first qualified position (found in package) based on display order.
  const defaultAssignmentPos = useMemo(() => {
    return availablePositions?.length > 0
      ? availablePositions?.sort((a, b) => a.displayOrder - b.displayOrder)?.[0]
      : null;
  }, [availablePositions]);

  const assignmentPos = defaultAssignmentPos;
  //const [assignmentPos, setAssignmentPos] = useState<IPosition>(defaultAssignmentPos);

  /** Generate crew position badge data. */
  const positionBadgeData = useMemo(() => {
    return { position: assignmentPos, filled: true, isInactive: !crewMember?.crewInfo?.isActive };
  }, [assignmentPos, crewMember?.crewInfo?.isActive]);

  const violationCheckStatus = useSelector(
    (state: IRootState) => state.crewSchedule.violationCheckStatus[crewMember?._id]
  );

  const [scanAnimation, setScanAnimation] = useState<"scan_errors" | "scan_active" | "scan_success" | "">("");

  useEffect(() => {
    // Set new animate state, only apply success state if previous state was "active" state.
    const newState =
      violationCheckStatus?.lastState === ViolationCheckState.Inprogress
        ? "scan_active"
        : violationCheckStatus?.lastState === ViolationCheckState.Failed
          ? "scan_errors"
          : violationCheckStatus?.lastState === ViolationCheckState.Success
            ? "scan_success"
            : "";
    setScanAnimation((prevState) => (prevState !== "scan_active" && newState === "scan_success" ? "" : newState));
  }, [violationCheckStatus?.lastState]);

  useEffect(() => {
    // If success animation is playing, reset it back to neutral after 5 seconds.
    const successAnimationTimer = setTimeout(() => {
      if (scanAnimation === "scan_success") setScanAnimation("");
    }, 5000);
    // clear timer on unmount if initiated
    return () => clearTimeout(successAnimationTimer);
  }, [scanAnimation]);

  /** Special handler for bid assignment mode, force expansion of crew row if context menu is opened. */
  const expandOnContext = useCallback(
    (crewMember: ICrewMember) => (event: React.MouseEvent) => {
      if (disableMouseEvents) {
        return;
      }
      if (!crewMember?.isExpanded) {
        toggleDisplayBidRows(crewMember?._id, true)(event);
      } else {
        handleContext(crewMember?._id)(event);
      }
    },
    [toggleDisplayBidRows, handleContext, disableMouseEvents]
  );

  const [awardingBids, setAwardingBids] = useState<boolean>(false);
  const handleUpdateCrewMemberBids = useCallback(
    async (toggleDone?: boolean) => {
      setAwardingBids(true);
      const awardedBids = returnAllAssignedBids(crewMemberBidPackage);
      const payload: IAwardBiddingPayload = {
        bidPackageId: selectedBidPackage?.bidPackage?._id,
        crewId: crewMember?._id,
        nfas: awardedBids.nfas,
        pairings: awardedBids.pairings,
        removeNFAs: awardedBids.removeNFAs,
        removePairings: awardedBids.removePairings,
        isDone: toggleDone ? !crewMemberBidPackage.isDone : crewMemberBidPackage.isDone,
        note: undefined,
      };
      const response = await updateCrewBids(payload, dispatch, toggleDone);
      if (response?.[crewMember?._id]) {
        const updatedCrewBids = { [crewMember?._id]: response?.[crewMember?._id] };
        dispatch(gotCrewMemberBids(updatedCrewBids));

        // PAIRINGS
        const successfullyAwardedPairing = (response?.awardedPairing as any)?.successfulIds?.length ?? 0;
        const successfullyRemovedPairing = (response?.removedPairing as any)?.successfulIds?.length ?? 0;
        const failedAwardedPairing = (response?.awardedPairing as any)?.failedIds?.length ?? 0;
        const failedRemovedPairing = (response?.removedPairing as any)?.failedIds?.length ?? 0;
        const pairingStatus =
          successfullyAwardedPairing > 0 ||
          successfullyRemovedPairing > 0 ||
          failedAwardedPairing > 0 ||
          failedRemovedPairing > 0
            ? `Pairings (
              ${successfullyAwardedPairing > 0 ? `${successfullyAwardedPairing} awarded, ` : ""}
              ${successfullyRemovedPairing > 0 ? `${successfullyRemovedPairing} unawarded, ` : ""}
              ${
                failedAwardedPairing + failedRemovedPairing > 0
                  ? `${failedAwardedPairing + failedRemovedPairing} failed`
                  : ""
              })`
            : "No pairings were updated";

        // NFAs
        const successfullyAwardedNFA = (response?.awardedNFA as any)?.successfulIds?.length ?? 0;
        const successfullyRemovedNFA = (response?.removedNFA as any)?.successfulIds?.length ?? 0;
        const failedAwardedNFA = (response?.awardedNFA as any)?.failedIds?.length ?? 0;
        const failedRemovedNFA = (response?.removedNFA as any)?.failedIds?.length ?? 0;
        const nfaStatus =
          successfullyAwardedNFA > 0 || successfullyRemovedNFA > 0 || failedAwardedNFA > 0 || failedRemovedNFA > 0
            ? `NFAs (
              ${successfullyAwardedNFA > 0 ? `${successfullyAwardedNFA} awarded, ` : ""}
              ${successfullyRemovedNFA > 0 ? `${successfullyRemovedNFA} unawarded, ` : ""}
              ${failedAwardedNFA + failedRemovedNFA > 0 ? `${failedAwardedNFA + failedRemovedNFA} failed` : ""})`
            : "No NFA's were updated";

        const totalSuccessfull =
          successfullyAwardedPairing + successfullyRemovedPairing + successfullyAwardedNFA + successfullyRemovedNFA;
        const totalFailed = failedAwardedPairing + failedRemovedPairing + failedAwardedNFA + failedRemovedNFA;
        const variant =
          totalSuccessfull > 0 && totalFailed === 0
            ? "success"
            : totalSuccessfull === 0 && totalFailed > 0
              ? "error"
              : "info";

        // Display new snackbar with updated info if theres anything to show.

        dispatch(
          openSnackbar({
            open: true,
            closeDelay: variant === "error" ? null : 10000,
            variant,
            message: `${pairingStatus} ${nfaStatus}`,
          })
        );
      }
      setAwardingBids(false);
    },
    [crewMemberBidPackage, crewMember, selectedBidPackage, dispatch]
  );

  // Reset all assigned bids in a crew members package.
  const handleResetAllAssignments = useCallback(() => {
    const allAssignedReset = resetAllAssignedBids(crewMemberBidPackage);
    dispatch(gotCrewMemberBids({ [crewMember?._id]: allAssignedReset }));
  }, [crewMemberBidPackage, dispatch, crewMember]);

  /** Load Crew bids for selected crew member and store them in redux */
  const [isLoadingCrewBids, setIsLoadingCrewBids] = useState<boolean>(false);
  const loadCrewBids = useCallback(async (): Promise<void> => {
    if (crewMember?._id && selectedBidPackage?.bidPackage?._id) {
      setIsLoadingCrewBids(true);
      const crewBids: IBidResponse = await getCrewBids(crewMember?._id, selectedBidPackage?.bidPackage?._id, dispatch);
      // console.log(crewBids, selectedCrew);
      if (crewBids?.[crewMember?._id]) {
        dispatch(gotCrewMemberBids(crewBids));
      }
      setIsLoadingCrewBids(false);
    }
  }, [crewMember, selectedBidPackage, dispatch]);

  /** Function that will dispatch action to toggle assignment of all pairings in selected crew bid rank. */
  const handleToggleCrewBidRank = useCallback(
    (rank: number, assign?: boolean) => {
      dispatch(toggleAssignmentCrewBidRank(crewMember?._id, rank, assign));
    },
    [dispatch, crewMember]
  );

  const allBidCount: number = useMemo(
    () => (crewMemberBidPackage ? bidEventCounts(crewMemberBidPackage) : 0),
    [crewMemberBidPackage]
  );

  const allAssignedBidCount: number = useMemo(
    () => (crewMemberBidPackage ? bidEventCounts(crewMemberBidPackage, undefined, true) : 0),
    [crewMemberBidPackage]
  );

  // Load or Refresh a selected crew members Crew Bids.
  const LoadCrewBidsBtn = useMemo(() => {
    return (
      <IconButton
        className="load_crew_bids_btn"
        size="small"
        tooltipProps={{
          title: `${!crewMemberBidPackage ? "Load" : "Refresh"} bids for ${crewMember?.firstName} ${
            crewMember?.lastName
          }`,
          style: { display: "flex" },
        }}
        onClick={debounce(loadCrewBids, 500)}
        disabled={isLoadingCrewBids}
      >
        {crewMemberBidPackage && !isLoadingCrewBids ? (
          <RefreshIcon style={{ width: 20, height: 20 }} />
        ) : isLoadingCrewBids ? (
          <CircularProgress style={{ width: 14, height: 14 }} />
        ) : (
          <DownloadIcon />
        )}
      </IconButton>
    );
  }, [crewMember, crewMemberBidPackage, isLoadingCrewBids, loadCrewBids]);

  // Saved all changed staged to current bids.
  const SaveStagedChanges = useMemo(() => {
    return (
      <IconButton
        className="save_awarded_bids_btn"
        size="small"
        tooltipProps={{
          title: `Award assigned bids for ${crewMember?.firstName} ${crewMember?.lastName}`,
          style: { display: "flex" },
        }}
        onClick={debounce(() => handleUpdateCrewMemberBids(false), 500)}
        disabled={allAssignedBidCount === 0 || awardingBids}
      >
        {!awardingBids ? <SaveIcon /> : <CircularProgress style={{ width: 14, height: 14 }} />}
      </IconButton>
    );
  }, [allAssignedBidCount, awardingBids, crewMember, handleUpdateCrewMemberBids]);

  // Component will only render if crew member is selected.
  const selectedCrewMemberDetails = useMemo(() => {
    if (!isSelected) return null;

    return (
      <Grid container wrap="nowrap">
        {allAssignedBidCount === 0 ? (
          LoadCrewBidsBtn
        ) : (
          <Grid container direction="column" alignItems="center" style={{ width: "auto" }}>
            {SaveStagedChanges}
            {allAssignedBidCount > 0 && (
              <Typography variant="caption" color="textPrimary" className="awarded_unsaved_count">
                ({allAssignedBidCount})
              </Typography>
            )}
          </Grid>
        )}
        {!displayInHeader && (
          <Grid container className="bidding_pref_ranks" direction="column" style={{ width: 120, margin: 8 }}>
            <Grid className="pref_rank_bar" container style={{ backgroundColor: theme.custom.forms.dirtyColor }}>
              <Typography className="label" variant="caption" color="textPrimary">
                Staging Row
              </Typography>
            </Grid>

            {bidRankSettings?.length
              ? bidRankSettings?.map((prefRank) => {
                  const rankBidCount = bidEventCounts(crewMemberBidPackage, prefRank.prefRank);
                  const rankAssignedBidCount = bidEventCounts(crewMemberBidPackage, prefRank.prefRank, true);

                  return (
                    <Grid
                      className="pref_rank_bar"
                      container
                      key={`pref_rank_${prefRank.prefRank}`}
                      style={{ backgroundColor: prefRank?.color }}
                    >
                      <Typography className="label" variant="caption">
                        {prefRank.label}
                      </Typography>
                      <IconButton
                        className="toggle_stage_assignment_btn"
                        tooltipProps={{
                          title: `Unstage assignment of all bids (${rankBidCount}) in ${prefRank.label}`,
                          style: { display: "flex" },
                        }}
                        size="small"
                        disabled={rankAssignedBidCount === 0}
                        onClick={() => handleToggleCrewBidRank(prefRank.prefRank, false)} // UNASSIGN ALL
                      >
                        <DoubleArrowIcon />
                      </IconButton>
                      <IconButton
                        className="toggle_stage_assignment_btn assign"
                        tooltipProps={{
                          title: `Stage Assignment of all bids (${rankBidCount}) in ${prefRank.label}`,
                          style: { display: "flex" },
                        }}
                        size="small"
                        disabled={rankAssignedBidCount === rankBidCount}
                        onClick={() => handleToggleCrewBidRank(prefRank.prefRank, true)} // ASSING ALL
                      >
                        <DoubleArrowIcon />
                      </IconButton>
                    </Grid>
                  );
                })
              : null}
          </Grid>
        )}
      </Grid>
    );
  }, [
    isSelected,
    allAssignedBidCount,
    LoadCrewBidsBtn,
    SaveStagedChanges,
    displayInHeader,
    theme.custom.forms.dirtyColor,
    bidRankSettings,
    crewMemberBidPackage,
    handleToggleCrewBidRank,
  ]);

  // Display Crew Member bidding status (in header) or rank information (in crew list)
  return displayInHeader ? (
    <Grid sx={crewBiddingInfoStyles(theme)} container direction="column">
      <Grid container className="crew_member" alignItems="center">
        {allAssignedBidCount === 0 ? LoadCrewBidsBtn : SaveStagedChanges}
        {!crewMemberBidPackage ? (
          <Typography variant="caption" style={{ marginLeft: 5 }}>{`${
            isLoadingCrewBids ? "Loading" : "Load"
          } bids for ${crewMember?.firstName} ${crewMember?.lastName}`}</Typography>
        ) : (
          <Typography
            variant="caption"
            style={{ marginLeft: 5 }}
          >{`${crewMember?.firstName} ${crewMember?.lastName}`}</Typography>
        )}
        {allBidCount > 0 && (
          <Grid container alignItems="center" style={{ width: "auto" }}>
            <Typography variant="caption" style={{ marginLeft: 5 }}>
              - {allBidCount} bids loaded
            </Typography>
            <EditRounded className={`edit_icon ${allAssignedBidCount > 0 ? "active" : ""}`} />
            <Typography variant="caption" className={`edit_text ${allAssignedBidCount > 0 ? "active" : ""}`}>
              ({allAssignedBidCount > 0 ? allAssignedBidCount : "no"} pending change{allAssignedBidCount > 1 ? "s" : ""}
              )
            </Typography>
          </Grid>
        )}
        <Popover
          sx={changeDetailsPopupStyles(theme)}
          anchorEl={anchorEl}
          anchorReference="anchorEl"
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
          open={!!anchorEl}
          onClose={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            event.stopPropagation();
            setAnchorEl(undefined);
          }}
        >
          <Grid className="crew_bid_change_status" container direction="column">
            {crewBidDetails(crewMemberBidPackage, hideNfaPriority)}
          </Grid>
        </Popover>
      </Grid>

      {crewMemberBidPackage && (
        <Grid container alignItems="center">
          <Button
            variant="outlined"
            className="show_change_details_btn"
            size="small"
            onClick={(event) => setAnchorEl(event?.currentTarget)}
          >
            {anchorEl ? "Hide Details" : "Show Details"}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            className="reset_all_changes_btn"
            size="small"
            disabled={allAssignedBidCount === 0}
            onClick={handleResetAllAssignments}
          >
            RESET
          </Button>

          <CheckboxField
            className="toggle_bidding_done_checkbox"
            label="Bidding Complete"
            name={`${crewMemberBidPackage?.bidPackageId}_done_state`}
            checked={crewMemberBidPackage?.isDone}
            disabled={!crewMemberBidPackage || allAssignedBidCount > 0}
            onChange={debounce(() => handleUpdateCrewMemberBids(true), 500)}
          />
        </Grid>
      )}
    </Grid>
  ) : (
    <Grid
      sx={{ ...crewBiddingInfoStyles(theme), ...crewMemberStyle }}
      className={classNames(
        "crew_details",
        isSelected && "selected",
        crewMemberBidPackage?.isDone && "bidding_done",
        disableMouseEvents && "presentation",
        scanAnimation
      )}
      key={crewMember?._id}
      item
      id={`position_block_${assignmentPos?.title}`}
    >
      <Grid
        container
        alignItems={"flex-start"}
        wrap="nowrap"
        onClick={(event) => !isSelected && toggleDisplayBidRows(crewMember?._id)(event)}
        onContextMenu={expandOnContext(crewMember)}
      >
        <Grid container direction="column" style={{ width: "auto" }}>
          <Grid item style={{ padding: 4 }}>
            <Tooltip
              enterDelay={500}
              placement="top"
              title={
                positionBadgeData?.isInactive
                  ? "Crew Member is Inactive"
                  : `${positionBadgeData?.position?.description}`
              }
            >
              <div>
                <PositionBadge {...positionBadgeData} />
              </div>
            </Tooltip>
          </Grid>

          <Grid item style={{ margin: "0px 5px" }}>
            <IconButton className="expand_crew_row_btn" onClick={toggleDisplayBidRows(crewMember?._id)} size="large">
              {isExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Grid>
        </Grid>

        <Grid
          container
          direction="column"
          className={`crew_member ${isSelected ? "selected" : ""}`}
          style={{ height: isSelected ? bidAssignmentHeight : baseRowHeight }}
          wrap="nowrap"
          justifyContent="flex-start"
        >
          <Grid container wrap="nowrap">
            <Grid
              container
              direction="column"
              style={{
                width: isSelected ? crewNameSpace : crewNameSpace * 2,
                marginRight: 2,
                marginTop: isSelected ? 4 : 0,
              }}
            >
              <CrewMemberName crewMemberName={crewMember.lastName} type="lastname" size={crewNameSpace} />
              <CrewMemberName crewMemberName={crewMember.firstName} type="firstname" size={crewNameSpace} />
              <Grid
                container
                direction="row"
                style={{ width: crewNameSpace, display: "flex", justifyContent: "space-between" }}
              >
                {crewMember?.operatingBase?.base?.ICAO && (
                  <span className="crew_base">{crewMember.operatingBase.base.ICAO}</span>
                )}
                {crewMember?.employeeId && <span className="crew_identity">{crewMember.employeeId}</span>}
              </Grid>
            </Grid>

            <Grid container direction="column">
              {displayPlannedCredit && (
                <Grid item style={{ width: "auto", marginRight: 0, marginLeft: "auto", marginTop: 1, marginBottom: 1 }}>
                  <Tooltip
                    sx={{ height: "10px", margin: "2px 0px" }}
                    disableFocusListener
                    disableTouchListener
                    placement="top"
                    title={
                      creditAllocatedByDay
                        ? "Sum of calculated credit falling inside the bid block & NFA default credit, for assigned & staged events. A pairing crossing the block boundary counts only its in-block days."
                        : "Sum of pairing planned credit & NFA default credit of assigned & staged events starting in bid block"
                    }
                  >
                    <Grid
                      container
                      sx={{
                        width: "auto",
                        fontSize: "10px",
                        lineHeight: "10px",
                        //fontWeight: "bold",
                        color: stagedCredit === 0 ? "white" : theme.custom.forms.dirtyColor,
                      }}
                    >
                      <Grid item>PC</Grid>
                      <Grid item sx={{ width: "20px", textAlign: "end" }}>
                        {totalPlannedCredits}
                      </Grid>
                    </Grid>
                  </Tooltip>
                </Grid>
              )}

              {!!totalAssignedNFATypes?.length &&
                displayNFATypes?.map((nfaType, index) => {
                  return index < 3 ? ( // Only return up to three NFA types.
                    <Grid
                      item
                      key={nfaType}
                      style={{ width: "auto", marginRight: 0, marginLeft: "auto", marginTop: 1, marginBottom: 1 }}
                    >
                      <Tooltip
                        sx={{ height: "10px", margin: "2px 0px" }}
                        disableFocusListener
                        disableTouchListener
                        placement="top"
                        title={`Sum of assigned & staged "${nfaType}" Non-Flying Assignments`}
                      >
                        <Grid
                          container
                          sx={{
                            width: "auto",
                            fontSize: "10px",
                            lineHeight: "10px",
                            //fontWeight: "bold",
                            color: stagedNFATypeCounts[index] === 0 ? "white" : theme.custom.forms.dirtyColor,
                          }}
                        >
                          <Grid item>{nfaType}</Grid>
                          <Grid item sx={{ width: "20px", textAlign: "end" }}>
                            {totalAssignedNFATypes[index]}
                          </Grid>
                        </Grid>
                      </Tooltip>
                    </Grid>
                  ) : (
                    <></>
                  );
                })}
            </Grid>

            {/*isSelected && <Grid container style={{ height: 40, width: 80 }}></Grid>*/}
          </Grid>
          {selectedCrewMemberDetails}
        </Grid>
      </Grid>
    </Grid>
  );
};
